import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { Button, Chip, Section, Typography } from 'src/lib'

interface IUploadSectionProps {
    selectedFile: File | null
    onFileSelect: (file: File) => void
    onClear: () => void
    onImport: () => void
    isLoading?: boolean
    fileTypeLabel?: string
}

const BUTTONS_WIDTH = 140

const UploadSection = ({ selectedFile, onFileSelect, onClear, onImport, isLoading = false, fileTypeLabel }: IUploadSectionProps) => {
    const [dragOver, setDragOver] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (!selectedFile && fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }, [selectedFile])

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        setDragOver(false)
        const file = e.dataTransfer.files[0]
        if (file) onFileSelect(file)
    }, [onFileSelect])

    const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) onFileSelect(file)
    }, [onFileSelect])

    const handleClear = () => {
        if (fileInputRef.current) fileInputRef.current.value = ''
        onClear()
    }

    return (
        <Section
            icon={<CloudUploadOutlinedIcon />}
            title={fileTypeLabel ?? 'Arquivo'}
            subtitle="Envie o arquivo exportado da plataforma"
        >
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', width: '100%', maxWidth: 520 }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box
                    onClick={!selectedFile ? () => fileInputRef.current?.click() : undefined}
                    onDragOver={(e) => { if (!selectedFile) { e.preventDefault(); setDragOver(true) } }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    sx={{
                        border: '1.5px dashed',
                        borderColor: dragOver ? 'primary.main' : 'rgba(162, 205, 237, 0.2)',
                        borderRadius: '4px',
                        bgcolor: dragOver ? 'rgba(162, 205, 237, 0.04)' : 'transparent',
                        px: 2.5,
                        py: 2,
                        minHeight: 72,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: selectedFile ? 'flex-start' : 'center',
                        gap: 1.5,
                        cursor: selectedFile ? 'default' : 'pointer',
                        transition: 'border-color 0.18s ease, background-color 0.18s ease',
                        ...(!selectedFile && { '&:hover': { borderColor: 'primary.main' } }),
                    }}
                >
                    {selectedFile ? (
                        <>
                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 26, color: 'primary.main', flexShrink: 0 }} />
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h3">{selectedFile.name}</Typography>
                                    {fileTypeLabel && <Chip label={fileTypeLabel} color="primary" size="small" />}
                                </Box>
                                <Typography variant="caption">
                                    {(selectedFile.size / 1024).toFixed(1)} KB
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <>
                            <CloudUploadOutlinedIcon sx={{ fontSize: 28, color: 'primary.main', opacity: 0.7 }} />
                            <Box>
                                <Typography variant="h3" color="quaternary">Arraste o arquivo aqui</Typography>
                                <Typography variant="caption">ou clique para selecionar</Typography>
                            </Box>
                        </>
                    )}
                </Box>
                <Typography variant="caption">Formatos aceitos: .xlsx, .csv</Typography>
                </Box>

                <Box sx={{
                    width: selectedFile ? BUTTONS_WIDTH : 0,
                    overflow: 'hidden',
                    transition: 'width 0.22s ease',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    justifyContent: 'center',
                }}>
                    <Button variant="contained" color="primary" onClick={onImport} fullWidth disabled={isLoading}>
                        {isLoading ? <CircularProgress size={16} color="inherit" /> : 'Enviar'}
                    </Button>
                    <Button variant="outlined" onClick={() => fileInputRef.current?.click()} fullWidth disabled={isLoading}>
                        Alterar
                    </Button>
                    <Button variant="text" color="neutral" onClick={handleClear} fullWidth disabled={isLoading}>
                        Remover
                    </Button>
                </Box>
            </Box>
            </Box>

            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFileInput}
            />
        </Section>
    )
}

export { UploadSection }
